/**
 * TODO - preamble
 */
import React, { useEffect } from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    getStatusLabel,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    LogoComponent,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    BirthDateComponent as BirthDate,
    MarketConsentWrapper,
    FirstResponderStatusComponent as FirstResponderStatus,
    FirstResponderOrganizationComponent as FirstResponderOrganization,
    PostalCodeComponent,
    FormFooterComponent as FormFooter,
    CountryComponentWrapper,
    RewardsRemainingComponent,
    VerificationService,
    FormSelectChoice,
    Organization,
    FirstResponderPersonalInfoViewModel,
    FirstResponderPersonalInfoResponse,
    VerificationStepsEnum,
    FirstResponderStatusDefaultMessagesEnum,
    isFormFilled,
    hasFailedInstantMatch,
    setRef,
    getSafe,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    FieldId,
    ErrorId,
} from '@sheerid/jslib';
import { MarinerNumber } from '../FormFields/MarinerNumber';
import { getQueryParamsByName } from '../../lib/utils/browser';

interface StepFirstResponderPersonalInfoComponentProps {
  verificationService: VerificationService;
  intl: any;
}

export const StepFirstResponderPersonalInfo = ({
    intl,
    verificationService,
}: StepFirstResponderPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as FirstResponderPersonalInfoViewModel;
    const { fieldValidationErrors }: {fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors} = verificationService;
    const verificationResponse = verificationService.verificationResponse as FirstResponderPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateFirstResponderViewModel = (
        key: keyof FirstResponderPersonalInfoViewModel,
        value: any,
    ) => {
        const nextState: FirstResponderPersonalInfoViewModel = produce(
            viewModel,
            (draft: FirstResponderPersonalInfoViewModel) => {
                (draft[key] as any) = value;
            },
        );
        verificationService.updateViewModel(nextState);
    };

    const getAvailableFirstResponderStatuses = (): FormSelectChoice[] => {
        const availableStatusesResponse = (
      verificationService.verificationResponse as FirstResponderPersonalInfoResponse
        ).availableStatuses;
        const availableStatuses: FormSelectChoice[] = [];

        availableStatusesResponse.forEach((status) => {
            availableStatuses.push({
                value: status,
                label: getStatusLabel(intl, status, FirstResponderStatusDefaultMessagesEnum),
            });
        });

        return availableStatuses;
    };

    const collectPostalCode = getSafe(() => verificationService.programTheme.openOrgSearchEnabled)
    && getSafe(() => viewModel.organization.id) === 0
    && getSafe(() => viewModel.organization.name, '').length > 0;

    if (collectPostalCode) {
        viewModel.postalCode = viewModel.postalCode ? viewModel.postalCode : ''; // TODO Fixme. Don't side-effect the viewModel
    } else if (viewModel.postalCode || viewModel.postalCode === '') {
        delete viewModel.postalCode; // TODO Fixme. Don't side-effect the viewModel
        updateFieldValidationErrorsByFieldId('postalCode', undefined, verificationService);
    }

    useEffect(() => {
        let marinerNumber: string = verificationService.viewModel.metadata?.captainsCircleNumber as string || getQueryParamsByName('marinerNumber') || '';
        if (verificationService?.previousVerificationResponse
            && verificationService?.previousVerificationResponse?.currentStep === 'docUpload') {
            // reset the marinerNumber if the user came from doc upload via max doc review failure
            marinerNumber = '';
        }
        updateFirstResponderViewModel('metadata', { ...verificationService.viewModel.metadata, captainsCircleNumber: marinerNumber });
    }, []);

    return (
        <div id="sid-step-active-first-responder-personal-info" className="sid-l-container">
            {failedInstantMatch ? (
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.title"
                            defaultMessage="Check Your Name, Email & Organization"
                        />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name, organization name, and email must match what is on record with your agency."
                        />
                    </div>
                </div>
            ) : (
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.title"
                            defaultMessage="Unlock this 1st Responder-Only Offer"
                            tagName="h1"
                        />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're an active first responder."
                        />
                    </div>
                    <div className="sid-header__how-verifying-works sid-l-horz-center">
                        <HowDoesVerifyingWork verificationService={verificationService} />
                        <RewardsRemainingComponent verificationService={verificationService} />
                    </div>
                </div>
            )}

            <CountryComponentWrapper verificationService={verificationService} viewModel={viewModel} />

            <MarinerNumber
                value={viewModel.metadata?.captainsCircleNumber}
                isErrored={!!fieldValidationErrors.captainsCircleNumber}
                errorId={fieldValidationErrors.captainsCircleNumber as ErrorId}
                onChange={(newValue: string) => {
                    updateFirstResponderViewModel('metadata', { ...verificationService.viewModel.metadata, captainsCircleNumber: newValue });
                    updateFieldValidationErrorsByFieldId('captainsCircleNumber' as FieldId, newValue, verificationService);
                }}
            />

            <FirstResponderStatus
                value={{
                    value: viewModel.status,
                    label: getStatusLabel(intl, viewModel.status, FirstResponderStatusDefaultMessagesEnum),
                }}
                isErrored={!!fieldValidationErrors.status}
                options={getAvailableFirstResponderStatuses()}
                onChange={(status: FormSelectChoice) => {
                    updateFirstResponderViewModel('status', status ? (status.value as string) : '');
                    updateFieldValidationErrorsByFieldId(
                        'status',
                        status ? (status.value as string) : '',
                        verificationService,
                    );
                }}
            />

            <FirstResponderOrganization
                isErrored={!!fieldValidationErrors.organization}
                onChange={(choice: Organization) => {
                    updateViewModelOrganization(choice, verificationService);
                    updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                }}
                value={(verificationService.viewModel as FirstResponderPersonalInfoViewModel).organization}
                verificationService={verificationService}
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={(newValue: string) => {
                        updateFirstResponderViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }}
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={(newValue: string) => {
                        updateFirstResponderViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }}
                />
            </div>

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateFirstResponderViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Needed to send you your unique code"
                    />
                )}
                onChange={(newValue: string) => {
                    updateFirstResponderViewModel('email', newValue);
                    updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                }}
                onKeyDown={(event: any) => handleEmailOnKeyDown(event)}
            />
            {verificationService.programTheme.isSmsNotifierConfigured
      || verificationService.programTheme.smsLoopEnabled ? (
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={(newValue: string) => {
                            updateFirstResponderViewModel('phoneNumber', newValue);
                            updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                        }}
                        selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
                    />
                ) : null}
            {collectPostalCode ? (
                <PostalCodeComponent
                    isErrored={!!fieldValidationErrors.postalCode}
                    onChange={(newValue: string) => {
                        updateFirstResponderViewModel('postalCode', newValue);
                        updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                    }}
                    value={viewModel.postalCode}
                />
            ) : null}

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateFirstResponderViewModel('metadata', {
                        ...verificationService.viewModel.metadata,
                        marketConsentValue: newValue,
                    });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(
                            viewModel,
                            verificationService,
                            VerificationStepsEnum.collectFirstResponderPersonalInfo,
                        )}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${
                            !isFormFilled(viewModel, verificationService.formValidationOptions)
                                ? 'sid-btn--disabled-like'
                                : ''
                        }`}
                        aria-labelledby="verify-status-text"
                        ref={(button) => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            {failedInstantMatch ? (
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            ) : (
                                <FormattedHTMLMessage
                                    id="verifyMyFirstResponderStatus"
                                    defaultMessage="Verify My First Responder Status"
                                />
                            )}
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepFirstResponderPersonalInfoComponent = injectIntl(StepFirstResponderPersonalInfo);
